import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { AppService } from '@Core/services/proprietary/app.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { DataSourceStatus } from '@Core/services/proprietary/datasource';
import { ObjectService } from '@Core/services/proprietary/object.service';
import {
    ResetDocViewerReportState,
    ResetState,
    SetCurrentDocument,
    SetCurrentReport,
    SetCurrentReportMode,
    SetCurrentReportPage,
    SetDocumentCollection,
    SetDocViewerReportCollectionConfig,
    SetDocViewerReportData,
    SetDocViewerRestoreDataMode,
    SetMergeDoc,
    SetStaticDoc
} from './document-viewer-object.actions';
import { DocumentViewerObjectState } from './document-viewer-object.state';

@Injectable()
export class DocumentViewerObjectService implements OnDestroy {
    private _currentDocID: any;

    documentDeleteNotifier = new Subject();

    constructor(
        private store: Store,
        private app: AppService,
        private objectService: ObjectService,
        private ds: DataService
    ) { }

    ngOnDestroy() { }

    reset() {
        this.store.dispatch(new ResetState());
    }

    viewDocument(document: any) {
        if (document) {
            this._currentDocID = document.Doc_ID;
        } else {
            this._currentDocID = null;
        }
        this.store.dispatch(new SetCurrentDocument(document));
        if (document && document.Doc_ID) {
            if (!this.app.DocPanelOpen) {
                this.app.DocPanelOpen = true;
                this.app.updateLayout();
            }
            if (!(document.Viewed_Ind && document.Viewed_Ind == 'Y')) {
                document.Viewed_Ind = 'Y'; //update flag on document node passed in -- update visual without doc retrieve.
            }
        }
    }

    async retieveAndViewDoc(docID: any) {
        if (!docID) {
            this._currentDocID = null;
            return;
        }

        let dsDocPdf = this.ds.addNewDatasource('Document', 'NO-CACHE');
        dsDocPdf.get({
            Doc_ID: docID,
            Doc_Format: 'None'
        });

        let docStatusSub = dsDocPdf.status$.subscribe((status) => {
            if (status) {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (docStatusSub) {
                            docStatusSub.unsubscribe();
                        }
                        let doc = dsDocPdf.objectNode;
                        this._currentDocID = doc.Doc_ID;
                        this.store.dispatch(new SetCurrentDocument(doc)).subscribe(() => {
                            if (!this.app.DocPanelOpen) {
                                this.app.DocPanelOpen = true;
                                this.app.updateLayout();
                            }
                            this.ds.deleteDatasource(dsDocPdf);
                        });
                        break;
                    case DataSourceStatus.Error:
                        if (docStatusSub) {
                            docStatusSub.unsubscribe();
                        }
                        this.ds.deleteDatasource(dsDocPdf);
                        break;
                }
            }
        });
    }

    viewReport(objectCode: string, criteria: any) {
        this._currentDocID = null;

        let reportName = '';
        let convertedCriteria: any = {};
        let criteriaName = '';

        let reportConfig = this.objectService.getObjectConfig(objectCode);
        if (reportConfig) {
            reportName = reportConfig.Object_Name;
        }

        for (var c in criteria) {
            //Build w/ and w/o "parm_" prefix (Ex: Contract_ID becomes both cr.parm_contract_id and cr.contract_id)
            criteriaName = 'cr.' + c.toLowerCase();
            convertedCriteria[criteriaName] = criteria[c];
            criteriaName = 'cr.parm_' + c.toLowerCase();
            convertedCriteria[criteriaName] = criteria[c];
        }

        this.store.dispatch(new SetCurrentReport(reportName, objectCode, convertedCriteria));

        if (!this.app.DocPanelOpen) {
            this.app.DocPanelOpen = true;
            this.app.Settings.DocPanel.DetailPanelOpen = false;
            this.app.updateLayout();
        } else {
            if (this.app.Settings.DocPanel.DetailPanelOpen == true) {
                this.app.Settings.DocPanel.DetailPanelOpen = false;
                this.app.updateLayout();
            }
        }
    }

    // this is used by the report page to update state
    // needed to handle criteria for data mode as well as pdf
    setReport(objectCode: string, criteria: any) {
        this._currentDocID = null;
        let repName = '';

        let reportConfig = this.objectService.getObjectConfig(objectCode);
        if (reportConfig) {
            repName = reportConfig.Object_Name;
        }

        this.store.dispatch(new SetCurrentReport(repName, objectCode, criteria));

        if (!this.app.DocPanelOpen) {
            this.app.DocPanelOpen = true;
            this.app.Settings.DocPanel.DetailPanelOpen = false;
            this.app.updateLayout();
        } else {
            if (this.app.Settings.DocPanel.DetailPanelOpen == true) {
                this.app.Settings.DocPanel.DetailPanelOpen = false;
                this.app.updateLayout();
            }
        }
    }

    viewMergeDoc(objectCode: string, criteria: any, mergeDocFormat: string) {
        this._currentDocID = null;
        let docName = '';

        let mergeDocConfig = this.objectService.getObjectConfig(objectCode);
        if (mergeDocConfig) {
            docName = mergeDocConfig.Object_Name;
        }

        this.store.dispatch(new SetMergeDoc(docName, objectCode, criteria, mergeDocFormat));
        if (!this.app.DocPanelOpen) {
            this.app.DocPanelOpen = true;
            this.app.Settings.DocPanel.DetailPanelOpen = false;
            this.app.updateLayout();
        } else {
            if (this.app.Settings.DocPanel.DetailPanelOpen == true) {
                this.app.Settings.DocPanel.DetailPanelOpen = false;
                this.app.updateLayout();
            }
        }
    }

    viewStaticDoc(objectCode: string, staticDocFormat: string) {
        this._currentDocID = null;
        let docName = '';

        let mergeDocConfig = this.objectService.getObjectConfig(objectCode);
        if (mergeDocConfig) {
            docName = mergeDocConfig.Object_Name;
        }

        this.store.dispatch(new SetStaticDoc(docName, objectCode, staticDocFormat));
        if (!this.app.DocPanelOpen) {
            this.app.DocPanelOpen = true;
            this.app.Settings.DocPanel.DetailPanelOpen = false;
            this.app.updateLayout();
        } else {
            if (this.app.Settings.DocPanel.DetailPanelOpen == true) {
                this.app.Settings.DocPanel.DetailPanelOpen = false;
                this.app.updateLayout();
            }
        }
    }

    get currentViewerDocID(): any {
        if (!this.app.DocPanelOpen) {
            return null;
        }
        return this._currentDocID;
    }

    setDocumentCollection(collection: []) {
        this.store.dispatch(new SetDocumentCollection(collection));
    }

    setCurrentReportMode(mode: boolean) {
        this.store.dispatch(new SetCurrentReportMode(mode));
    }

    setCurrentReportPage(reportName: string, objectCode: string, reportPage: any) {
        this.store.dispatch(new SetCurrentReportPage(reportName, objectCode, reportPage));
    }

    setDocViewerReportData(rowData: any) {
        this.store.dispatch(new SetDocViewerReportData(rowData));
    }

    setDocViewerReportCollectionConfig(collectionConfig: any) {
        this.store.dispatch(new SetDocViewerReportCollectionConfig(collectionConfig));
    }

    setDocViewerRestoreDataMode(restore: boolean) {
        this.store.dispatch(new SetDocViewerRestoreDataMode(restore));
    }

    resetDocViewerReportState() {
        this.store.dispatch(new ResetDocViewerReportState());
    }

    getCurrentReport() {
        return this.store.selectSnapshot(DocumentViewerObjectState.report);
    }

    getDocViewerReportCollectionConfig() {
        let collConfig = this.store.selectSnapshot(DocumentViewerObjectState.reportCollectionConfig);
        return collConfig;
    }

    getDocViewerReportData() {
        let collData = this.store.selectSnapshot(DocumentViewerObjectState.reportData);
        return collData;
    }

    getDocViewerRestoreDataMode() {
        let restoreDataMode = this.store.selectSnapshot(DocumentViewerObjectState.restoreDataMode);
        return restoreDataMode;
    }

    getCurrentReportPage() {
        let currentPage = this.store.selectSnapshot(DocumentViewerObjectState.currentReportPage);
        return currentPage;
    }

    getDocProtectedInd() {
        let docProtectedInd = this.store.selectSnapshot(DocumentViewerObjectState.currentDocProtectedInd);
        return docProtectedInd;
    }

    openDocViewer() {
        if (!this.app.DocPanelOpen) {
            this.app.DocPanelOpen = true;
            this.app.Settings.DocPanel.DetailPanelOpen = false;
            this.app.updateLayout();
        } else {
            if (this.app.Settings.DocPanel.DetailPanelOpen == true) {
                this.app.Settings.DocPanel.DetailPanelOpen = false;
                this.app.updateLayout();
            }
        }
    }
}
